import { z } from 'zod';

export enum LookbackDurationUnit {
  Days = 'days',
  Weeks = 'weeks',
  Months = 'months',
  Years = 'years',
}

export const LookbackDurationSchema = z.object({
  value: z.coerce.number().min(0, { message: 'Duration value cannot be negative' }),
  unit: z.nativeEnum(LookbackDurationUnit, { message: 'Unit reqd' }),
});
export type LookbackDuration = z.infer<typeof LookbackDurationSchema>;

/**
 * Note - this does not account for DST transitions, number of days in a month, or leap years.
 * Regarding DST transitions:
 * 1 day in milliseconds is not the same for a DST transition.
 * 1 day for the switch from standard time to DST is 23 hours.
 * 1 day for the switch from DST to standard time is 25 hours.
 * If you need to account for correct date durations, use date-fns Date math utilities,
 * such as https://date-fns.org/v4.1.0/docs/add
 */
export function getLookbackDurationInMilliseconds(
  value: number,
  unit: LookbackDurationUnit
): number {
  const durations: { [key in LookbackDurationUnit]: number } = {
    days: 24 * 60 * 60 * 1000, // Approximate (Not valid for DST transitions)
    weeks: 7 * 24 * 60 * 60 * 1000, // Approximate (Not valid for DST transitions)
    months: 30 * 7 * 24 * 60 * 60 * 1000, // Approximate (Not valid for DST transitions, and not valid for all months)
    years: 365 * 24 * 60 * 60 * 1000, // Approximate (Not valid for leap years)
  };
  return value * durations[unit];
}

/**
 * Note - this does not account for DST transitions, number of days in a month, or leap years.
 * Regarding DST transitions:
 * 1 day in milliseconds is not the same for a DST transition.
 * 1 day for the switch from standard time to DST is 23 hours.
 * 1 day for the switch from DST to standard time is 25 hours.
 * If you need to account for correct date durations, use date-fns Date math utilities,
 * such as https://date-fns.org/v4.1.0/docs/add
 */
export function convertDurationToMilliseconds(duration: LookbackDuration): number {
  return getLookbackDurationInMilliseconds(duration.value, duration.unit);
}

export function LookbackDurationSchemaWithMax(max: LookbackDuration) {
  const maxMilliseconds = getLookbackDurationInMilliseconds(max.value, max.unit);
  return LookbackDurationSchema.refine(
    (arg) => {
      const milliseconds = getLookbackDurationInMilliseconds(arg.value, arg.unit);
      return milliseconds <= maxMilliseconds;
    },
    {
      message: `Max value is ${max.value} ${max.unit}`,
      path: ['value'],
    }
  );
}
